/**
 * NestJS API Service
 * Phase 3: Backend API Integration
 *
 * バックエンドのNestJS APIと通信するためのサービスレイヤー
 * - Reservations API
 * - Services API
 * - Staff API
 * - Customers API
 */

import { apiClient } from './apiClient';
import type { ExtendedReservation } from '@/types/salonboard';
import type { Customer } from '@/utils/localStorage';

// ========== Reservations API ==========

export interface ReservationFilters {
  date?: string;
  status?: 'PENDING' | 'CONFIRMED' | 'WAITING_RECEPTION' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';
  customerId?: string;
  staffId?: string;
}

export interface CreateReservationDto {
  customerId: string;
  staffId: string;
  serviceId: string;
  reservationDate: string; // yyyy-MM-dd
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  notes?: string;
  storeId: number;
}

export interface UpdateReservationDto {
  customerId?: string;
  staffId?: string;
  serviceId?: string;
  reservationDate?: string;
  startTime?: string;
  endTime?: string;
  notes?: string;
}

export class NestJSApiService {
  /**
   * 予約一覧取得（フィルタ可能）
   */
  static async getReservations(filters?: ReservationFilters): Promise<ExtendedReservation[]> {
    const response = await apiClient.get<ExtendedReservation[]>('/api/v1/reservations', filters);
    return response.data;
  }

  /**
   * 予約詳細取得
   */
  static async getReservation(id: string): Promise<ExtendedReservation> {
    const response = await apiClient.get<ExtendedReservation>(`/api/v1/reservations/${id}`);
    return response.data;
  }

  /**
   * 新規予約作成
   */
  static async createReservation(dto: CreateReservationDto): Promise<ExtendedReservation> {
    const response = await apiClient.post<ExtendedReservation>('/api/v1/reservations', dto);
    return response.data;
  }

  /**
   * 予約更新
   */
  static async updateReservation(id: string, dto: UpdateReservationDto): Promise<ExtendedReservation> {
    const response = await apiClient.put<ExtendedReservation>(`/api/v1/reservations/${id}`, dto);
    return response.data;
  }

  /**
   * 予約ステータス更新
   */
  static async updateReservationStatus(
    id: string,
    status: 'PENDING' | 'CONFIRMED' | 'WAITING_RECEPTION' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW'
  ): Promise<ExtendedReservation> {
    const response = await apiClient.patch<ExtendedReservation>(
      `/api/v1/reservations/${id}/status`,
      { status }
    );
    return response.data;
  }

  /**
   * 予約キャンセル
   */
  static async cancelReservation(id: string, reason?: string): Promise<ExtendedReservation> {
    const response = await apiClient.patch<ExtendedReservation>(
      `/api/v1/reservations/${id}/cancel`,
      { reason }
    );
    return response.data;
  }

  /**
   * 予約削除
   */
  static async deleteReservation(id: string): Promise<void> {
    await apiClient.delete(`/api/v1/reservations/${id}`);
  }

  /**
   * 空き予約枠取得
   */
  static async getAvailableTimeSlots(
    staffId: string,
    date: string,
    duration: number
  ): Promise<{ startTime: string; endTime: string }[]> {
    const response = await apiClient.get<{ startTime: string; endTime: string }[]>(
      `/api/v1/reservations/staff/${staffId}/available-slots`,
      { date, duration }
    );
    return response.data;
  }

  /**
   * 顧客の今後の予約取得
   */
  static async getCustomerUpcomingReservations(customerId: string): Promise<ExtendedReservation[]> {
    const response = await apiClient.get<ExtendedReservation[]>(
      `/api/v1/reservations/customer/${customerId}/upcoming`
    );
    return response.data;
  }

  /**
   * 期間指定予約取得
   */
  static async getReservationsByDateRange(startDate: string, endDate: string): Promise<ExtendedReservation[]> {
    const response = await apiClient.get<ExtendedReservation[]>('/api/v1/reservations/date-range', {
      startDate,
      endDate,
    });
    return response.data;
  }

  // ========== Services API ==========

  /**
   * サービス一覧取得
   */
  static async getServices(storeId?: number): Promise<any[]> {
    const response = await apiClient.get<any[]>('/api/v1/services', storeId ? { storeId } : undefined);
    return response.data;
  }

  /**
   * サービス作成
   */
  static async createService(service: any): Promise<any> {
    const response = await apiClient.post<any>('/api/v1/services', service);
    return response.data;
  }

  /**
   * サービス更新
   */
  static async updateService(id: string, service: Partial<any>): Promise<any> {
    const response = await apiClient.put<any>(`/api/v1/services/${id}`, service);
    return response.data;
  }

  /**
   * サービス削除
   */
  static async deleteService(id: string): Promise<void> {
    await apiClient.delete(`/api/v1/services/${id}`);
  }

  // ========== Staff API ==========

  /**
   * スタッフ一覧取得
   */
  static async getStaff(storeId?: number): Promise<any[]> {
    const response = await apiClient.get<any[]>('/api/v1/staff', storeId ? { storeId } : undefined);
    return response.data;
  }

  /**
   * スタッフ作成
   */
  static async createStaff(staff: any): Promise<any> {
    const response = await apiClient.post<any>('/api/v1/staff', staff);
    return response.data;
  }

  /**
   * スタッフ更新
   */
  static async updateStaff(id: string, staff: Partial<any>): Promise<any> {
    const response = await apiClient.put<any>(`/api/v1/staff/${id}`, staff);
    return response.data;
  }

  /**
   * スタッフ削除
   */
  static async deleteStaff(id: string): Promise<void> {
    await apiClient.delete(`/api/v1/staff/${id}`);
  }

  /**
   * スタッフスケジュール取得
   */
  static async getStaffSchedule(staffId: string, date: string): Promise<ExtendedReservation[]> {
    const response = await apiClient.get<ExtendedReservation[]>(
      `/api/v1/staff/${staffId}/schedule`,
      { date }
    );
    return response.data;
  }

  // ========== Customers API (将来拡張用) ==========

  /**
   * 顧客一覧取得
   */
  static async getCustomers(): Promise<Customer[]> {
    const response = await apiClient.get<Customer[]>('/api/v1/customers');
    return response.data;
  }

  /**
   * 顧客作成
   */
  static async createCustomer(customer: Omit<Customer, 'id' | 'createdAt' | 'updatedAt' | 'customerCode'>): Promise<Customer> {
    const response = await apiClient.post<Customer>('/api/v1/customers', customer);
    return response.data;
  }

  /**
   * 顧客更新
   */
  static async updateCustomer(id: number, customer: Partial<Customer>): Promise<Customer> {
    const response = await apiClient.put<Customer>(`/api/v1/customers/${id}`, customer);
    return response.data;
  }

  /**
   * 顧客削除
   */
  static async deleteCustomer(id: number): Promise<void> {
    await apiClient.delete(`/api/v1/customers/${id}`);
  }
}

export default NestJSApiService;
